import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import firebase from 'firebase/compat/app';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { BuilderWaiverGroup, WaiverField, WaiverFieldType, WaiverFormConfig, ProductWaiverAssignment, ProductGroupWaiverAssignment, ReplacementProductAssignment, RequiredWaiverSummary, WaiverVersionSummary, WAIVER_FIELD_TEMPLATES, DEFAULT_MINOR_AGE_THRESHOLD, MAX_VARIANTS_PER_GROUP } from './waiver-builder.model';
import { ProductsService } from 'src/app/services/products.service';
import { ProductV1 } from 'src/app/models/storage/product.model';
import { FirestoreService } from 'src/app/v2/services/firestore.service';
import { Collection } from 'src/app/v2/models/collection-reference.model';
import { CurrentUserService } from 'src/app/services/current-user.service';
import { RentalService } from 'src/app/services/rental.service';
import {
  classifyRequiredWaiverRef,
  collectRequiredWaiverRefIds,
  minorAgeThresholdSeed,
  requiredProductGroupIds,
  resolveWaiverRefForParticipant,
} from './waiver-rules';

/**
 * Lightweight legacy-waiver shape returned from the partner-side list. Drops
 * the heavy `pdfContent` blob to keep the list response small; the full doc
 * is fetched on demand at conversion time via the cached `raw` reference.
 */
export interface LegacyWaiverSummary {
  id: string;
  name: string;
  isPdf: boolean;
  hasContent: boolean;
  /** Set after a successful conversion — points at the new builder waiver. */
  convertedToWaiverId?: string;
  /** Raw doc contents for the conversion heuristic. Trimmed so the cache stays cheap. */
  raw: { title?: string; waiverTitle?: string; name?: string; content?: string; waiverPDF?: boolean; pdfContent?: string };
}

/** How {@link WaiverBuilderService.getRequiredWaiverRefIds} treats unreadable product groups. */
export interface RequiredWaiverLoadOptions {
  /**
   * Reject when a product group cannot be read, instead of treating its
   * waivers as absent. For compliance checks, where a missing requirement
   * would otherwise read as satisfied.
   */
  strict?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class WaiverBuilderService {

  private savedWaivers: WaiverFormConfig[] = [];
  public savedWaivers$ = new BehaviorSubject<WaiverFormConfig[]>([]);

  /**
   * Cache of the current company's EXPLICIT waiver-group docs (from
   * `builderWaiverGroups`). Waivers with no `groupId` are represented
   * as implicit single-waiver groups on the fly by
   * {@link getEffectiveGroups} — they don't sit in this array. Kept
   * private so subscribers work off `waiverGroups$` (which emits the
   * combined effective view including implicits).
   */
  private waiverGroups: BuilderWaiverGroup[] = [];
  /** Emits the combined effective list (explicit + implicit) on every
   *  mutation. Renders the UI's "Waivers" list. */
  public waiverGroups$ = new BehaviorSubject<BuilderWaiverGroup[]>([]);
  private loadedGroupsCompanyId: string | null = null;

  /**
   * Global minor-age threshold for the loaded company. Emits the current
   * value on every mutation; consumers (waiver builder settings modal,
   * sign / preview / canvas surfaces) subscribe here rather than reading
   * per-waiver fields — the threshold is company-wide as of the migration
   * away from `WaiverFormConfig.minorAgeThreshold`. Falls back to
   * `DEFAULT_MINOR_AGE_THRESHOLD` until {@link loadMinorAgeThreshold}
   * resolves for the current company.
   */
  public minorAgeThreshold$ = new BehaviorSubject<number>(DEFAULT_MINOR_AGE_THRESHOLD);
  private loadedThresholdCompanyId: string | null = null;
  /**
   * Company whose threshold document could not be READ on the most recent
   * {@link loadMinorAgeThreshold} call. Distinct from "the company has no
   * threshold configured", which is a legitimate state that falls back to
   * {@link DEFAULT_MINOR_AGE_THRESHOLD}.
   */
  private thresholdReadFailedForCompanyId: string | null = null;

  constructor(
    private productsService: ProductsService,
    private firestoreService: FirestoreService,
    private currentUserService: CurrentUserService,
    private rentalService: RentalService,
    private afs: AngularFirestore,
    private storage: AngularFireStorage,
  ) {}

  /**
   * Uploads a data-URL-encoded PDF as the template attached to a builder
   * waiver.
   *
   * Path convention piggybacks on the old waiver-editor's flat
   * `pdfWaiver/` folder — a known-writable pattern under the deployed
   * dev/prod storage rules — but namespaces builder assets with a
   * `builder_` filename prefix so they can't collide with legacy
   * single-waiver blobs:
   *
   *   `companies/{companyId}/pdfWaiver/builder_{waiverId}_{uploadId}.pdf`
   *
   * Every upload gets its own object and never overwrites an earlier one.
   * Archived versions (`/versions/{N}`) keep the `pdfContent` URL they were
   * published with, so the file behind that URL must never change. The
   * upload runs on file selection, before Save, so a shared path would
   * also rewrite the live version's PDF on an abandoned edit. The id is
   * per upload rather than the version number because the version is only
   * decided at Save. Waivers uploaded before this point at the legacy
   * `builder_{waiverId}.pdf`, which is left untouched.
   *
   * We tried a distinct `builderWaivers/` subpath first and hit
   * `storage/unauthorized` — the deployed rules whitelist paths the
   * local `storage.rules` file no longer reflects, and updating that
   * requires a separate `firebase deploy --only storage` pass. Reusing
   * the existing folder sidesteps the deploy step entirely.
   *
   * Returns the download URL. Caller writes it onto
   * `formConfig.pdfContent` and re-saves the waiver — the upload only
   * lands the bytes in Storage.
   *
   * `waiverId` must exist (i.e. the waiver has been saved at least
   * once). Uploading before the first save would produce an orphan file
   * at a random id — the builder UI gates the button on `formConfig.id`.
   */
  async uploadWaiverPdf(waiverId: string, dataUrl: string): Promise<string> {
    const companyId = this.currentUserService.currentUser?.companyId;
    if (!companyId) throw new Error('No active company; cannot upload waiver PDF.');
    if (!waiverId) throw new Error('waiverId is required.');
    const ref = this.storage.ref(this.waiverPdfStoragePath(companyId, waiverId, crypto.randomUUID()));
    const task = await ref.putString(dataUrl, 'data_url');
    return await task.ref.getDownloadURL();
  }

  /**
   * Deletes the legacy fixed-path object (`builder_{waiverId}.pdf`).
   * Currently unused. Archived versions may still reference that file, so
   * don't wire this up without checking every version first. Best-effort —
   * a missing object resolves cleanly so the caller can safely null the
   * field even if the file was already gone.
   */
  async removeWaiverPdf(waiverId: string): Promise<void> {
    const companyId = this.currentUserService.currentUser?.companyId;
    if (!companyId) throw new Error('No active company; cannot remove waiver PDF.');
    if (!waiverId) return;
    const ref = this.storage.ref(this.legacyWaiverPdfStoragePath(companyId, waiverId));
    try {
      await firstValueFrom(ref.delete());
    } catch (err) {
      // Storage returns object-not-found when there's nothing to delete.
      // Treat as success so the caller can clear the URL field either way.
      const code = (err as { code?: string })?.code;
      if (code !== 'storage/object-not-found') throw err;
    }
  }

  private waiverPdfStoragePath(companyId: string, waiverId: string, uploadId: string): string {
    // Flat file, `builder_` prefix — see uploadWaiverPdf's block comment
    // for the rationale (piggybacks on a known-writable folder).
    return `companies/${companyId}/pdfWaiver/builder_${waiverId}_${uploadId}.pdf`;
  }

  /** Pre-versioning path shared by every version of a waiver. Never written now. */
  private legacyWaiverPdfStoragePath(companyId: string, waiverId: string): string {
    return `companies/${companyId}/pdfWaiver/builder_${waiverId}.pdf`;
  }

  /** Returns the (defensively copied) waivers array attached to a product. */
  private getEffectiveWaivers(product: ProductV1): string[] {
    return Array.isArray(product.waivers) ? [...product.waivers] : [];
  }

  /** Persists a product's waivers array to Firestore. */
  private async updateProductWaivers(productId: string, waivers: string[]): Promise<void> {
    await this.productsService.updateProduct(productId, { waivers });
  }

  getSavedWaivers(): WaiverFormConfig[] {
    return [...this.savedWaivers];
  }

  /**
   * One-shot fetch of every legacy waiver doc under
   * `companies/{companyId}/waivers`. Used by the waiver-builder list to
   * surface a "Legacy Waivers" section alongside the builder waivers.
   *
   * Returns lightweight summaries that include the raw doc contents the
   * conversion heuristic needs — pdfContent is included so the conversion
   * can detect PDFs, but callers that just want the list metadata can
   * ignore the `raw` field.
   */
  async getLegacyWaivers(companyId?: string): Promise<LegacyWaiverSummary[]> {
    const id = companyId || this.currentUserService.currentUser?.companyId;
    if (!id) return [];
    const snap = await this.afs
      .collection('companies').doc(id)
      .collection('waivers').ref.get();
    return snap.docs.map(d => {
      const raw = (d.data() || {}) as LegacyWaiverSummary['raw'] & { convertedToWaiverId?: string };
      const isPdf = !!raw.waiverPDF && !!raw.pdfContent;
      return {
        id: d.id,
        name: raw.title || raw.waiverTitle || raw.name || 'Untitled Legacy Waiver',
        isPdf,
        hasContent: isPdf || !!(raw.content && raw.content.trim()),
        convertedToWaiverId: raw.convertedToWaiverId,
        raw,
      };
    });
  }

  /**
   * Heuristic legacy → builder converter. Produces a fresh
   * `WaiverFormConfig` draft that the partner can preview-edit-confirm
   * before saving via {@link saveWaiver}.
   *
   * Strategy:
   *   1. Body content — PDF detected → empty `pdf` field placeholder (the
   *      original PDF blob doesn't transfer cleanly into the new field
   *      shape; the partner re-uploads). HTML detected → `textBody`
   *      populated with the stripped plaintext.
   *   2. Identification fields — keyword scan against the lowercased text
   *      (`name`, `email`, `phone`, etc.) decides which fields land in.
   *   3. Guardian — keyword scan adds a guardian name and
   *      `guardianSignature`.
   *   4. Always appends a `signature` at the end.
   *
   * Heuristic only — false positives and misses are expected; the preview
   * modal is where the partner cleans up.
   */
  convertLegacyToBuilder(legacy: LegacyWaiverSummary): WaiverFormConfig {
    const fields: WaiverField[] = [];
    let order = 0;

    // Legacy PDF waivers don't have body text to extract — the partner has
    // to retype/recreate the content as text. We still seed the standard
    // input fields (name/signature/etc.) below from keyword scanning the
    // (likely empty) content; the partner adds a textBody manually.
    if (!legacy.isPdf) {
      const text = this.stripHtmlToText(legacy.raw.content || '');
      fields.push({
        id: this.generateId(),
        type: 'textBody',
        label: 'Waiver Text',
        required: false,
        order: order++,
        placeholder: '',
        value: text,
      });
    }

    const haystack = (legacy.raw.content || '').toLowerCase();
    const has = (...needles: string[]) => needles.some(n => haystack.includes(n));

    if (has('printed name', 'print name', 'full name', 'your name', 'participant name', ' name ')) {
      fields.push(this.createField('name', order++));
    }
    if (has('email')) {
      fields.push(this.createField('email', order++));
    }
    if (has('phone', 'cell', 'telephone', 'mobile')) {
      fields.push(this.createField('phone', order++));
    }
    // Guardian capture only — no minor-declaration widget any more. Whether
    // a guardian actually has to sign is decided from the participant's
    // date of birth at sign time, not asked on the form.
    if (has('minor', 'under 18', 'under eighteen', 'under age', 'parent', 'guardian')) {
      fields.push(this.createGuardianNameField(order++));
      fields.push(this.createGuardianSignatureField(order++));
    }

    // Always append the primary signature at the bottom.
    fields.push(this.createField('signature', order++));

    const config: WaiverFormConfig = {
      name: legacy.name || 'Converted Legacy Waiver',
      version: 1,
      versionLabel: this.generateVersionLabel(legacy.name || 'Converted', 1, new Date()),
      fields,
    };
    return config;
  }

  /**
   * After a successful conversion, stamp the legacy doc so the list shows
   * "✓ Converted" instead of the Convert button on subsequent loads. Best
   * effort — failure is logged but shouldn't block the new waiver itself.
   */
  async markLegacyAsConverted(legacyId: string, newWaiverId: string, companyId?: string): Promise<void> {
    const id = companyId || this.currentUserService.currentUser?.companyId;
    if (!id || !legacyId || !newWaiverId) return;
    await this.afs
      .collection('companies').doc(id)
      .collection('waivers').doc(legacyId)
      .update({ convertedToWaiverId: newWaiverId });
  }

  /**
   * Strip HTML tags and decode entities for legacy-waiver content that
   * needs to land in a textBody field. Uses a DOMParser so script/style
   * blocks are dropped and visible text + line breaks survive.
   */
  private stripHtmlToText(html: string): string {
    if (!html) return '';
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    // Drop script/style noise.
    doc.querySelectorAll('script, style').forEach(el => el.remove());
    // Insert blank lines between block-level elements so paragraph breaks survive.
    const blockSelectors = 'p, div, br, li, h1, h2, h3, h4, h5, h6, tr';
    doc.querySelectorAll(blockSelectors).forEach(el => el.append('\n'));
    const text = (doc.body.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
    return text;
  }

  /**
   * Every waiver reference attached to `products`, unclassified: direct
   * `product.waivers[]` entries plus each product group's `waivers[]`.
   * Ids may be builder waiver ids, group ids or legacy ids. Used where the
   * raw attachment matters more than classification (e.g. deciding which
   * envelope items a product removal orphaned).
   */
  async getRequiredWaiverRefIds(
    products: Array<{ id: string; waivers?: string[]; productGroupID?: string }>,
    options: RequiredWaiverLoadOptions = {},
  ): Promise<string[]> {
    // Product groups are the source of truth for waiver attachment
    // (`productGroup/{id}.waivers[]`); per-product arrays are still read
    // as a bridge. Each group doc is fetched once. Note the Firestore
    // collection is `productGroup` (singular) — matches
    // Collection.ProductGroup and firestore.rules.
    //
    // A group that does not EXIST contributes nothing either way. A group
    // read that FAILS does too by default (best-effort, as the checkout and
    // cart flows rely on), but rejects under `strict`: its waivers are
    // unknown, not absent, and a compliance check must not read "complete".
    const groupSnaps = await Promise.all(
      requiredProductGroupIds(products).map(id =>
        this.afs.collection('productGroup').doc(id).ref.get().catch((err: unknown) => {
          if (options.strict) throw err;
          return null;
        }),
      ),
    );
    const groupWaivers = groupSnaps.map(snap =>
      snap && snap.exists ? (snap.data() as { waivers?: unknown } | undefined)?.waivers : undefined,
    );
    return collectRequiredWaiverRefIds(products, groupWaivers);
  }

  /**
   * Given a set of products (e.g. from a cart), return the deduplicated list of
   * required waivers. Under the group model each id in `product.waivers[]` may
   * be a group id OR a bare waiver id; the classification order is:
   *
   *   1. Matches a builder waiver → return that waiver (auto-migrated groups
   *      hit this path because `group.id === waiver.id`).
   *   2. Matches an explicit waiver group → return a summary keyed on the
   *      group id, with the group's active variant supplying the
   *      `builderConfig` payload for display. Callers pass this id to
   *      {@link resolveWaiverForParticipant} to get the specific variant
   *      per participant at envelope-build time.
   *   3. Legacy (companies/{companyId}/waivers/{id}) — unchanged.
   *
   * Without step 2 a product attached to a NEW explicit group (a
   * `.doc()`-allocated group id, not a `.doc(waiverId)` one) silently
   * drops from the required-waivers list because it doesn't match any
   * savedWaivers row and falls through to the legacy lookup.
   */
  async getRequiredWaiversForProducts(
    products: Array<{ id: string; waivers?: string[]; productGroupID?: string }>,
    companyId: string,
    options: RequiredWaiverLoadOptions = {},
  ): Promise<RequiredWaiverSummary[]> {
    const waiverIds = await this.getRequiredWaiverRefIds(products, options);

    const results: RequiredWaiverSummary[] = [];
    for (const id of waiverIds) {
      const ref = classifyRequiredWaiverRef(id, this.savedWaivers, this.waiverGroups);
      if (ref.kind === 'builder') {
        results.push({
          id,
          name: ref.name,
          isLegacy: false,
          builderConfig: { ...ref.waiver, fields: [...ref.waiver.fields] },
        });
        continue;
      }

      // Group-id ref (explicit multi-variant group whose `.doc()` id
      // doesn't match any waiver). Use the group's active variant for
      // the display config; the id we return is the GROUP id so the
      // sign-time resolver can swap in a minor variant when needed.
      if (ref.kind === 'group') {
        results.push({
          id,
          name: ref.name,
          isLegacy: false,
          builderConfig: ref.activeVariant
            ? { ...ref.activeVariant, fields: [...ref.activeVariant.fields] }
            : undefined,
        });
        continue;
      }

      if (!companyId) continue;

      try {
        const legacyDoc = await this.rentalService.getWaiverById(companyId, id);
        if (legacyDoc) {
          results.push({
            id,
            name: legacyDoc.title || legacyDoc.waiverTitle || legacyDoc.name || 'Waiver',
            isLegacy: true,
            legacyDoc,
          });
        }
      } catch (err) {
        console.error(`Failed to load legacy waiver ${id}:`, err);
      }
    }
    return results;
  }

  /**
   * Hydrates `savedWaivers` from `builderWaivers` filtered by the current
   * partner's company. Idempotent for the same company id — repeat calls
   * are no-ops until `companyId` changes (e.g. user switches companies) —
   * unless `forceRefresh` is set (see {@link refreshWaiverRules}).
   *
   * Components that render the partner-side waiver list must call this on
   * init; the service does not auto-load because the constructor runs
   * before the auth user is hydrated.
   */
  async loadCompanyWaivers(companyId?: string, options: { forceRefresh?: boolean } = {}): Promise<void> {
    const id = companyId || this.currentUserService.currentUser?.companyId;
    if (!id) return;
    if (!options.forceRefresh && this.loaded && this.loadedCompanyId === id) return;

    const snap = await this.afs
      .collection('builderWaivers').ref
      .where('companyId', '==', id).get();

    this.savedWaivers = snap.docs.map(d => this.hydrateWaiver(d.id, d.data()));
    this.savedWaivers$.next([...this.savedWaivers]);
    this.loaded = true;
    this.loadedCompanyId = id;
  }

  /**
   * Re-reads the company's waivers and waiver groups from Firestore,
   * replacing the per-company caches. Call it immediately before creating
   * NEW envelope items — the caches load once per session, so a page left
   * open while a waiver was published, or its adult/minor assignment
   * changed, in another tab would otherwise pin the old version or the
   * old variant.
   *
   * Both are needed: variant resolution walks waiver → group → variant and
   * only accepts variants it holds, so a newly assigned minor waiver must
   * be in the waiver list as well as the group. Existing envelope items
   * are never touched — they keep the version they were pinned to.
   *
   * Two queries. Rejects if either read fails, so a caller can refuse to
   * create items rather than pin a version it could not confirm.
   */
  async refreshWaiverRules(companyId: string): Promise<void> {
    await this.loadCompanyWaivers(companyId, { forceRefresh: true });
    await this.loadCompanyWaiverGroups(companyId, { forceRefresh: true });
  }

  /**
   * Upserts a waiver, persisting to `builderWaivers/{id}` and refreshing
   * the local cache. Returns the persisted shape (includes the
   * Firestore-allocated id on first save).
   */
  async saveWaiver(config: WaiverFormConfig): Promise<WaiverFormConfig> {
    const companyId = this.currentUserService.currentUser?.companyId;
    if (!companyId) {
      throw new Error('No active company; cannot save waiver.');
    }

    const docRef = config.id
      ? this.afs.collection('builderWaivers').doc(config.id)
      : this.afs.collection('builderWaivers').doc();

    const saved: WaiverFormConfig = {
      ...config,
      id: docRef.ref.id,
      updatedAt: new Date(),
    };
    // `minorAgeThreshold` is now a company-wide setting; strip any legacy
    // per-waiver value that might still ride along on an old cached config
    // so we don't re-persist deprecated state on every save.
    delete (saved as { minorAgeThreshold?: number }).minorAgeThreshold;

    // Firestore payload — same as `saved` but with an explicit
    // FieldValue.delete() for `pdfContent` when the user cleared it.
    // Reason: the app is configured with `ignoreUndefinedProperties:
    // true`, so a plain `pdfContent: undefined` would be SKIPPED on a
    // merge-set — the previous URL would stay stuck on the doc. Only
    // FieldValue.delete() actually removes the field.
    const payload: Record<string, unknown> = { ...saved, companyId };
    if (!saved.pdfContent) {
      payload['pdfContent'] = firebase.firestore.FieldValue.delete();
    }
    // Retire the deprecated company-singleton `isMinorWaiver` flag on
    // write. Under the group model the minor variant is chosen per-group
    // via `BuilderWaiverGroup.minorWaiverId`; the old flag no longer
    // drives behavior. Explicitly delete it so re-saves clean up old
    // docs and readers never see a stale singleton.
    payload['isMinorWaiver'] = firebase.firestore.FieldValue.delete();
    delete (saved as { isMinorWaiver?: boolean }).isMinorWaiver;

    await docRef.set(payload, { merge: true });

    const idx = this.savedWaivers.findIndex(w => w.id === saved.id);
    if (idx > -1) this.savedWaivers[idx] = saved;
    else this.savedWaivers.push(saved);
    this.savedWaivers$.next([...this.savedWaivers]);
    // A first-time save produces a solo waiver with no groupId — it
    // shows up as an implicit group via getEffectiveGroups. Re-emit the
    // combined stream so the list reflects the new row without a reload.
    this.emitEffectiveGroups();
    return saved;
  }

  // ==========================================================================
  // Waiver version history
  //
  // Version history lives in a subcollection at
  //   `builderWaivers/{waiverId}/versions/{versionNumber}`
  // Each doc is a full snapshot of the WaiverFormConfig as of the moment
  // the partner clicked "Publish new version". Save alone doesn't create
  // snapshots — it just persists the working draft to the parent doc.
  // Publish (a) snapshots the CURRENT draft as v{current}, (b) bumps the
  // version number on the parent doc, and (c) stamps publishedAt.
  //
  // Envelope items pin `waiverVersion` at creation so signed views load
  // the exact snapshot the customer agreed to, not whatever the current
  // draft looks like. That wiring lives at each envelope-creation site.
  // ==========================================================================

  /**
   * Archives the CURRENT parent-doc state into the versions subcollection
   * at its current version number, then returns the next version number
   * the caller should use when writing new content. Called BEFORE the
   * new draft is written to the parent doc — the snapshot has to capture
   * the pre-edit state so historical envelope items (which pin the
   * outgoing version number) still resolve to the content the customer
   * actually signed against.
   *
   * The earlier implementation snapshotted the INCOMING config after
   * saveWaiver had already overwritten the parent doc, so /versions/{N}
   * ended up holding the edited content instead of the pre-edit one —
   * a signed-view pinned to version N then showed the new edits. This
   * variant fixes that by reading the parent doc itself.
   *
   * Returns `{ archivedVersion, nextVersion }`. archivedVersion is
   * undefined for brand-new waivers (no parent to archive); nextVersion
   * is 1 in that case so first-save land at v1.
   */
  async archiveCurrentVersion(
    waiverId: string,
  ): Promise<{ archivedVersion?: number; nextVersion: number; publishedAt: Date }> {
    const companyId = this.currentUserService.currentUser?.companyId;
    if (!companyId) throw new Error('No active company; cannot archive waiver version.');
    const now = new Date();
    if (!waiverId) return { nextVersion: 1, publishedAt: now };

    const docRef = this.afs.collection('builderWaivers').doc(waiverId).ref;
    return this.afs.firestore.runTransaction(async (transaction) => {
      const snap = await transaction.get(docRef);
      if (!snap.exists) {
        // Brand new — no parent state to archive, first write lands at v1.
        return { nextVersion: 1, publishedAt: now };
      }
      const data = (snap.data() ?? {}) as Record<string, unknown>;
      const currentVersion = typeof data['version'] === 'number' && (data['version'] as number) > 0
        ? (data['version'] as number)
        : 1;

      // Historical snapshots are immutable (Firestore rules allow create
      // only). An existing `/versions/{N}` means an earlier Save archived
      // this version and then failed writing the parent; the parent can't
      // change content without bumping its version, so that snapshot is
      // already the right one. Skip the write and carry on with the retry.
      const versionRef = docRef.collection('versions').doc(String(currentVersion));
      const existing = await transaction.get(versionRef);
      if (!existing.exists) {
        // Snapshot the parent doc EXACTLY as it currently sits — every field
        // the sign/view surfaces render off, at the version number stamped
        // on the outgoing state. `ignoreUndefinedProperties: true` handles
        // any sparse-optional fields on the raw doc.
        const snapshot = {
          ...data,
          id: waiverId,
          companyId,
          version: currentVersion,
          // archivedAt captures WHEN this snapshot was frozen (the moment
          // the partner started a new Save). Distinct from `publishedAt`
          // on the parent doc, which stamps the NEW version's landing time.
          archivedAt: now,
        };
        transaction.set(versionRef, snapshot);
      }

      return {
        archivedVersion: currentVersion,
        nextVersion: currentVersion + 1,
        publishedAt: now,
      };
    });
  }

  /**
   * Lists every published version for a waiver, newest first. Used by
   * the History dropdown in the builder header — only the summary
   * fields are needed to render the list, so we don't hydrate full
   * snapshot bodies here. Empty array for waivers that have never
   * been published (or never existed).
   */
  async getWaiverVersionSummaries(waiverId: string): Promise<WaiverVersionSummary[]> {
    if (!waiverId) return [];
    const snap = await this.afs
      .collection('builderWaivers').doc(waiverId)
      .collection('versions').ref.get();
    const toDate = (v: unknown): Date | undefined => {
      if (!v) return undefined;
      if (v instanceof Date) return v;
      const withToDate = v as { toDate?: () => Date };
      if (typeof withToDate.toDate === 'function') return withToDate.toDate();
      return undefined;
    };
    const rows: WaiverVersionSummary[] = snap.docs.map(doc => {
      const data = doc.data() as Record<string, unknown>;
      return {
        version: typeof data['version'] === 'number' ? (data['version'] as number) : Number(doc.id) || 0,
        versionLabel: typeof data['versionLabel'] === 'string' ? (data['versionLabel'] as string) : '',
        name: typeof data['name'] === 'string' ? (data['name'] as string) : '',
        publishedAt: toDate(data['publishedAt']),
      };
    });
    rows.sort((a, b) => b.version - a.version);
    return rows;
  }

  /**
   * Loads a specific historical version as a WaiverFormConfig. Used by
   * the History dropdown to preview an old version read-only and by
   * envelope-side "view signed waiver" surfaces to render exactly what
   * the customer agreed to. Returns undefined when the version doesn't
   * exist (e.g. legacy waiver saved before the snapshot infrastructure
   * was added, or a caller passing a bad version number).
   */
  async getWaiverVersion(waiverId: string, version: number): Promise<WaiverFormConfig | undefined> {
    if (!waiverId || !Number.isFinite(version) || version <= 0) return undefined;
    const snap = await this.afs
      .collection('builderWaivers').doc(waiverId)
      .collection('versions').doc(String(version)).ref.get();
    if (!snap.exists) return undefined;
    return this.hydrateWaiver(waiverId, snap.data() || {});
  }

  // ==========================================================================
  // Waiver group management
  //
  // Groups are named bundles of interchangeable waiver variants. Products
  // attach to a group; at sign time the group resolves to ONE variant
  // based on the participant (adult uses `activeWaiverId`, minor uses
  // `minorWaiverId` when set). Groups are persisted at
  // `builderWaiverGroups/{groupId}`; each variant carries a back-reference
  // (`WaiverFormConfig.groupId`).
  //
  // Auto-wrap: waivers with no `groupId` are presented as implicit
  // single-waiver groups whose id equals the waiver id. Implicit groups
  // never touch Firestore — they exist only in the effective view — so
  // legacy `products.waivers[]` refs continue to resolve unchanged.
  // ==========================================================================

  /**
   * Loads explicit `builderWaiverGroups/*` docs for the current company
   * into the cache. Idempotent per company id. Callers on the partner
   * side should invoke this alongside {@link loadCompanyWaivers} — both
   * caches are needed to render the effective view.
   */
  /**
   * Loads the company's global minor-age threshold into
   * {@link minorAgeThreshold$}. Idempotent per company id.
   *
   * Seeds on first load when the company doc has no
   * `waiverMinorAgeThreshold` set: takes the mode (most-common value) of
   * `WaiverFormConfig.minorAgeThreshold` across the loaded builder waivers,
   * writes it back to the company doc, and emits it. If no per-waiver
   * values are set either, falls back to {@link DEFAULT_MINOR_AGE_THRESHOLD}
   * without a write — a partner-set value later will still stick because
   * the seed only fires when the field is missing.
   *
   * Requires {@link loadCompanyWaivers} to have run first so the seed pool
   * is populated; otherwise the seed always falls back to the default.
   *
   * @param companyId Company whose threshold to load. Defaults to the
   *   signed-in user's company.
   * @param forceRefresh Skip the per-company memo and re-read the company
   *   doc. Callers that are about to CLASSIFY a new participant pass true:
   *   the memo is fine for painting a field label, but a partner whose tab
   *   has been open since before the threshold was changed (in another tab,
   *   or by a colleague) would otherwise stamp a stale value onto a
   *   participant being created right now. Existing participants are
   *   unaffected either way — their threshold is snapshotted at issuance.
   */
  async loadMinorAgeThreshold(companyId?: string, forceRefresh = false): Promise<void> {
    const id = companyId || this.currentUserService.currentUser?.companyId;
    if (!id) return;
    if (this.loadedThresholdCompanyId === id && !forceRefresh) return;

    let raw: number | undefined;
    try {
      const snap = await this.afs.collection('companies').doc(id).ref.get();
      const data = (snap.exists ? snap.data() : undefined) as { waiverMinorAgeThreshold?: unknown } | undefined;
      const val = data?.waiverMinorAgeThreshold;
      if (typeof val === 'number' && Number.isFinite(val) && val > 0) {
        raw = val;
      }
      this.thresholdReadFailedForCompanyId = null;
    } catch (err) {
      console.error('Failed to read waiverMinorAgeThreshold from company doc:', err);
      // Remembered so callers about to CLASSIFY a participant can refuse
      // rather than silently classify against the fallback — a company
      // that never configured a threshold is a different situation from
      // one whose document we couldn't read. See
      // {@link minorAgeThresholdReadFailed}.
      this.thresholdReadFailedForCompanyId = id;
    }

    if (raw === undefined) {
      const seeded = this.computeThresholdSeed();
      if (seeded !== undefined) {
        try {
          await this.afs.collection('companies').doc(id).update({
            waiverMinorAgeThreshold: seeded,
          });
          raw = seeded;
        } catch (err) {
          // Seed write is best-effort — emit the computed value anyway
          // so the UI reflects the shop's historical setting even if the
          // company doc update failed (typically an auth / rules edge).
          console.error('Failed to seed company waiverMinorAgeThreshold:', err);
          raw = seeded;
        }
      }
    }

    const next = raw ?? DEFAULT_MINOR_AGE_THRESHOLD;
    this.loadedThresholdCompanyId = id;
    this.minorAgeThreshold$.next(next);
  }

  /**
   * Persists a new global minor-age threshold for the loaded company and
   * emits it to subscribers. Validation matches the input control range
   * (1–25 integer) so a bogus value can't slip past the modal into
   * Firestore.
   */
  async setMinorAgeThreshold(value: number): Promise<void> {
    const companyId = this.loadedThresholdCompanyId || this.currentUserService.currentUser?.companyId;
    if (!companyId) throw new Error('No active company; cannot set waiver minor age threshold.');
    const rounded = Math.round(Number(value));
    if (!Number.isFinite(rounded) || rounded < 1 || rounded > 25) {
      throw new Error('Minor age threshold must be between 1 and 25.');
    }
    await this.afs.collection('companies').doc(companyId).update({
      waiverMinorAgeThreshold: rounded,
    });
    this.loadedThresholdCompanyId = companyId;
    this.minorAgeThreshold$.next(rounded);
  }

  /**
   * Shows or hides a whole group on the public Waiver Kiosk page. Writes
   * `hiddenFromKiosk` to every variant of the group (an implicit solo group
   * has one), so the flag is read off the exact waiver being signed and the
   * group stays consistent. A plain field update: no version bump, no
   * snapshot, no `updatedAt` change.
   */
  async setKioskVisibility(group: BuilderWaiverGroup, hidden: boolean): Promise<void> {
    const targets = this.savedWaivers.filter(w => group.waiverIds.includes(w.id));
    if (targets.length === 0) return;
    const batch = this.afs.firestore.batch();
    for (const waiver of targets) {
      batch.update(this.afs.collection('builderWaivers').doc(waiver.id).ref, {
        hiddenFromKiosk: hidden ? true : firebase.firestore.FieldValue.delete(),
      });
    }
    await batch.commit();
    for (const waiver of targets) {
      if (hidden) waiver.hiddenFromKiosk = true;
      else delete waiver.hiddenFromKiosk;
    }
    this.savedWaivers$.next([...this.savedWaivers]);
    this.emitEffectiveGroups();
  }

  /**
   * Sync accessor for the current global threshold. Prefer subscribing to
   * {@link minorAgeThreshold$} for reactive UI; this exists for callers
   * that need a one-shot read (validation, field labels stamped at build
   * time). Always returns a valid number — the observable is seeded to
   * {@link DEFAULT_MINOR_AGE_THRESHOLD} before {@link loadMinorAgeThreshold}
   * resolves.
   */
  getMinorAgeThreshold(): number {
    return this.minorAgeThreshold$.getValue();
  }

  /**
   * True when the most recent {@link loadMinorAgeThreshold} for this company
   * could not read the company document at all, so
   * {@link getMinorAgeThreshold} is reporting the fallback rather than a
   * resolved value.
   *
   * Callers that merely DISPLAY the threshold can ignore this. Callers about
   * to CLASSIFY a participant should check it and refuse: persisting
   * `minorAgeThresholdAtIssue: 18` when 18 was a fallback rather than the
   * shop's setting would bake a wrong historical fact into the envelope,
   * and a shop configured at 21 would silently mis-classify its minors.
   *
   * A company with no `waiverMinorAgeThreshold` configured is NOT a failure
   * — the documented default applies and this returns false.
   */
  minorAgeThresholdReadFailed(companyId?: string): boolean {
    const id = companyId || this.currentUserService.currentUser?.companyId;
    if (!id) return false;
    return this.thresholdReadFailedForCompanyId === id;
  }

  /**
   * Picks a seed value for a first-time-load company by taking the mode
   * of `minorAgeThreshold` values across the currently loaded builder
   * waivers. Ties break toward the LARGER value (more conservative for
   * minor-consent gating). Returns undefined when no waivers carry an
   * explicit threshold — caller falls back to the default without a write.
   */
  private computeThresholdSeed(): number | undefined {
    return minorAgeThresholdSeed(this.savedWaivers);
  }

  async loadCompanyWaiverGroups(companyId?: string, options: { forceRefresh?: boolean } = {}): Promise<void> {
    const id = companyId || this.currentUserService.currentUser?.companyId;
    if (!id) return;
    const alreadyLoaded = this.loadedGroupsCompanyId === id;
    if (alreadyLoaded && !options.forceRefresh) return;

    const snap = await this.afs
      .collection('builderWaiverGroups').ref
      .where('companyId', '==', id).get();

    this.waiverGroups = snap.docs.map(d => this.hydrateGroup(d.id, (d.data() ?? {}) as Record<string, unknown>));
    this.loadedGroupsCompanyId = id;
    this.emitEffectiveGroups();

    // A forced re-read only refreshes the cache; the one-time migration
    // below already ran on this company's first load.
    if (alreadyLoaded) return;

    // One-time auto-migration: any waiver in the current company's cache
    // that predates the group model (no `groupId`, and no existing group
    // doc backing it) gets a fresh `builderWaiverGroups/*` doc created
    // with the SAME id as the waiver. See migrateOrphanWaiversToGroups
    // for the id-matching rationale. Idempotent — no orphans = no-op.
    // Failure is logged but non-fatal; the read-time fallback (implicit
    // groups via getEffectiveGroups) still keeps everything working.
    try {
      await this.migrateOrphanWaiversToGroups(id);
    } catch (err) {
      console.error('Waiver-group auto-migration failed (continuing):', err);
    }
    // Second cleanup pass — scrub dangling waiver ids from groups.
    // Pre-fix delete paths left group.waiverIds entries pointing at
    // deleted waivers, so the sidebar's "N variants" count read too
    // high. This scans every group and rewrites any that carry ids
    // that no longer resolve to a real waiver.
    try {
      await this.scrubDanglingWaiverIds();
    } catch (err) {
      console.error('Waiver-group dangling-id scrub failed (continuing):', err);
    }
  }

  /**
   * Repairs groups whose `waiverIds` contain ids that no longer
   * correspond to real waivers (residue from delete paths that ran
   * before `deleteWaiver` cleaned up group refs). For each such group:
   *
   *   - Rewrites `waiverIds` to only the ids that resolve.
   *   - Reassigns `activeWaiverId` if it pointed at a dropped id.
   *   - Clears `minorWaiverId` if it pointed at a dropped id.
   *   - Deletes the group entirely if scrubbing empties it out.
   *
   * Idempotent — clean groups are untouched. Batched with a chunk cap
   * so a company with hundreds of groups doesn't blow the 500-op
   * Firestore batch limit.
   */
  private async scrubDanglingWaiverIds(): Promise<void> {
    const knownIds = new Set(this.savedWaivers.map(w => w.id).filter((x): x is string => !!x));
    interface ScrubOp {
      group: BuilderWaiverGroup;
      cleaned: string[];
      nextActive?: string;
      clearMinor: boolean;
      shouldDelete: boolean;
    }
    const ops: ScrubOp[] = [];
    for (const group of this.waiverGroups) {
      const cleaned = group.waiverIds.filter(id => knownIds.has(id));
      if (cleaned.length === group.waiverIds.length) continue; // nothing to scrub
      const activeDangling = !knownIds.has(group.activeWaiverId);
      const minorDangling = !!group.minorWaiverId && !knownIds.has(group.minorWaiverId);
      ops.push({
        group,
        cleaned,
        nextActive: activeDangling ? cleaned[0] : undefined,
        clearMinor: minorDangling,
        shouldDelete: cleaned.length === 0,
      });
    }
    if (ops.length === 0) return;

    const now = new Date();
    const chunkSize = 200;
    for (let i = 0; i < ops.length; i += chunkSize) {
      const batch = this.afs.firestore.batch();
      for (const op of ops.slice(i, i + chunkSize)) {
        const ref = this.afs.collection('builderWaiverGroups').doc(op.group.id).ref;
        if (op.shouldDelete) {
          batch.delete(ref);
          continue;
        }
        const patch: Record<string, unknown> = {
          waiverIds: op.cleaned,
          updatedAt: now,
        };
        if (op.nextActive) patch['activeWaiverId'] = op.nextActive;
        if (op.clearMinor) patch['minorWaiverId'] = firebase.firestore.FieldValue.delete();
        batch.update(ref, patch);
      }
      await batch.commit();
    }

    // Cache mirror.
    for (const op of ops) {
      if (op.shouldDelete) {
        this.waiverGroups = this.waiverGroups.filter(g => g.id !== op.group.id);
        continue;
      }
      op.group.waiverIds = op.cleaned;
      if (op.nextActive) op.group.activeWaiverId = op.nextActive;
      if (op.clearMinor) delete op.group.minorWaiverId;
      op.group.updatedAt = now;
    }
    this.emitEffectiveGroups();
    console.info(`[waiver-groups] Scrubbed dangling waiver ids from ${ops.length} group(s).`);
  }

  /**
   * One-time migration for legacy waivers created before the group
   * model existed. For every waiver in the cache that:
   *   - Belongs to `companyId`,
   *   - Has no `groupId` back-reference, AND
   *   - Isn't already a member of an explicit group,
   * this creates a `builderWaiverGroups/*` doc **using the waiver's own
   * id as the group id** (via `.doc(waiver.id).set(...)`, not
   * `.doc().set(...)` — the auto-generated id path).
   *
   * Why match the ids: `products.waivers[]` today holds waiver ids.
   * Making the new group's id equal to the waiver id means those
   * existing product refs transparently resolve as group refs — no
   * product-doc migration is required for this feature to ship.
   *
   * Idempotent — subsequent invocations detect zero orphans and return
   * early. Batches Firestore writes so hundreds of waivers migrate in a
   * single round trip; chunks at 200 waivers (400 ops) to stay under
   * the 500-op batch cap with headroom.
   */
  async migrateOrphanWaiversToGroups(companyId?: string): Promise<void> {
    const id = companyId || this.currentUserService.currentUser?.companyId;
    if (!id) return;

    const explicitMemberIds = new Set(this.waiverGroups.flatMap(g => g.waiverIds));
    const orphans = this.savedWaivers.filter(w => {
      if (!w.id) return false;
      if (w.groupId) return false;
      if (explicitMemberIds.has(w.id)) return false;
      // Also skip if a group doc already exists at the waiver's id
      // (partial migration state — a prior run wrote the group but the
      // waiver update failed). We'll fix the backlink below.
      if (this.waiverGroups.some(g => g.id === w.id)) return false;
      return true;
    });

    if (orphans.length === 0) return;

    const now = new Date();
    const chunkSize = 200;
    for (let i = 0; i < orphans.length; i += chunkSize) {
      const chunk = orphans.slice(i, i + chunkSize);
      const batch = this.afs.firestore.batch();
      for (const waiver of chunk) {
        if (!waiver.id) continue;
        // Group doc at the waiver's own id — .doc(waiver.id) selects
        // that specific document rather than auto-generating one.
        const groupRef = this.afs.collection('builderWaiverGroups').doc(waiver.id).ref;
        batch.set(groupRef, {
          companyId: id,
          name: waiver.name || 'Untitled Group',
          waiverIds: [waiver.id],
          activeWaiverId: waiver.id,
          createdAt: now,
          updatedAt: now,
        });
        // Backlink `groupId` on the waiver doc so future loads short-
        // circuit past this migration entirely.
        batch.update(this.afs.collection('builderWaivers').doc(waiver.id).ref, {
          groupId: waiver.id,
          updatedAt: now,
        });
      }
      await batch.commit();
    }

    // Reflect migration in the caches — subscribers see the new groups
    // and updated backlinks without waiting for the next reload.
    for (const waiver of orphans) {
      if (!waiver.id) continue;
      this.waiverGroups.push({
        id: waiver.id,
        companyId: id,
        name: waiver.name || 'Untitled Group',
        waiverIds: [waiver.id],
        activeWaiverId: waiver.id,
        createdAt: now,
        updatedAt: now,
      });
      waiver.groupId = waiver.id;
    }
    this.savedWaivers$.next([...this.savedWaivers]);
    this.emitEffectiveGroups();
    console.info(
      `[waiver-groups] Auto-migrated ${orphans.length} orphan waiver(s) into explicit groups ` +
      `(group id === waiver id).`,
    );
  }

  /**
   * Combined effective view: explicit groups + one implicit
   * single-waiver group per orphaned waiver (no `groupId`). The
   * `waiverGroups$` stream carries this same shape.
   */
  getEffectiveGroups(): BuilderWaiverGroup[] {
    // Skip explicit groups with no members. Empty groups can appear
    // transiently if a batch write partially failed, or if a future
    // op splices a group's last member without immediately deleting
    // the shell doc. Filtering here keeps a stray empty group from
    // rendering as a phantom row with no variants.
    const explicit = this.waiverGroups.filter(g => g.waiverIds.length > 0);
    const explicitMemberIds = new Set(explicit.flatMap(g => g.waiverIds));
    const implicit: BuilderWaiverGroup[] = this.savedWaivers
      .filter(w => w.id && !w.groupId && !explicitMemberIds.has(w.id))
      .map(w => this.makeImplicitGroup(w));
    return [...explicit, ...implicit];
  }

  /**
   * Resolves a product-attached waiver reference (usually a raw waiver
   * id from `products.waivers[]`) to the specific waiver id that should
   * appear on the participant's envelope item. Traversal:
   *
   *   1. If the id points at a waiver AND that waiver has a `groupId`,
   *      load the group → return `minorWaiverId` when the participant
   *      is a minor and it's set, otherwise `activeWaiverId`.
   *   2. If the waiver exists but carries no `groupId`, it's an implicit
   *      solo group — return the waiver id as-is. Its own guardian
   *      fields carry the minor case inside the sign form if present.
   *   3. If no such waiver exists but an explicit group with that id
   *      does (rare transitional case where a product's ref was set to
   *      a group id directly), apply the same swap on the group.
   *   4. Unknown id — return undefined; caller drops it.
   */
  resolveWaiverForParticipant(waiverRefId: string, isMinor: boolean): string | undefined {
    return resolveWaiverRefForParticipant(waiverRefId, isMinor, this.savedWaivers, this.waiverGroups);
  }

  /**
   * The cached builder waivers and EXPLICIT group docs, exactly as
   * {@link getRequiredWaiversForProducts} classifies requirement refs and
   * {@link resolveWaiverForParticipant} resolves them — for callers that
   * apply the same rules to documents already in memory. Not
   * `waiverGroups$`: that is the display view, which drops explicit groups
   * with no members and adds implicit ones, so classifying against it
   * disagrees with the read-based path.
   */
  waiverRuleSources(): { waivers: ReadonlyArray<WaiverFormConfig>; groups: ReadonlyArray<BuilderWaiverGroup> } {
    return { waivers: this.savedWaivers, groups: this.waiverGroups };
  }

  /**
   * Promotes a solo waiver into an explicit group. The waiver becomes
   * the first (and initially only) member; `activeWaiverId` is set to
   * it. `minorWaiverId` starts unset — the partner chooses one after
   * adding a second variant.
   */
  async createGroupFromWaiver(waiverId: string, name?: string): Promise<BuilderWaiverGroup> {
    const companyId = this.currentUserService.currentUser?.companyId;
    if (!companyId) throw new Error('No active company; cannot create waiver group.');
    const waiver = this.savedWaivers.find(w => w.id === waiverId);
    if (!waiver || !waiver.id) throw new Error('Waiver not found.');
    if (waiver.groupId) throw new Error('Waiver is already in a group.');

    const docRef = this.afs.collection('builderWaiverGroups').doc();
    const now = new Date();
    const group: BuilderWaiverGroup = {
      id: docRef.ref.id,
      companyId,
      name: (name && name.trim()) || waiver.name || 'Untitled Group',
      waiverIds: [waiver.id],
      activeWaiverId: waiver.id,
      createdAt: now,
      updatedAt: now,
    };

    const batch = this.afs.firestore.batch();
    batch.set(docRef.ref, group);
    batch.update(this.afs.collection('builderWaivers').doc(waiver.id).ref, {
      groupId: docRef.ref.id,
      updatedAt: now,
    });
    await batch.commit();

    this.waiverGroups.push(group);
    waiver.groupId = docRef.ref.id;
    this.emitEffectiveGroups();
    this.savedWaivers$.next([...this.savedWaivers]);
    return group;
  }

  /** Renames an explicit group. No-op for implicit groups (rename by
   *  editing the waiver's name via saveWaiver instead). */
  async renameGroup(groupId: string, name: string): Promise<void> {
    const group = this.waiverGroups.find(g => g.id === groupId);
    if (!group) return;
    const trimmed = (name || '').trim();
    if (!trimmed || trimmed === group.name) return;
    const now = new Date();
    await this.afs.collection('builderWaiverGroups').doc(groupId).update({
      name: trimmed,
      updatedAt: now,
    });
    group.name = trimmed;
    group.updatedAt = now;
    this.emitEffectiveGroups();
  }

  /**
   * Lightweight name-only rename for a single waiver variant. Skips
   * the full validate → increment-version → save pipeline in
   * {@link saveWaiver} because a rename shouldn't bump the semver-ish
   * `version` counter or invalidate consumers relying on identity.
   *
   * Also updates the implicit-group name in the effective view: an
   * implicit group's `name` is derived from its waiver's `name`, so
   * re-emitting `waiverGroups$` here keeps the sidebar in sync
   * without a reload.
   */
  async renameWaiver(waiverId: string, name: string): Promise<void> {
    const waiver = this.savedWaivers.find(w => w.id === waiverId);
    if (!waiver) return;
    const trimmed = (name || '').trim();
    if (!trimmed || trimmed === waiver.name) return;
    const now = new Date();
    await this.afs.collection('builderWaivers').doc(waiverId).update({
      name: trimmed,
      updatedAt: now,
    });
    waiver.name = trimmed;
    waiver.updatedAt = now;
    // Refresh the versionLabel so the sidebar's `v{version} · {date}`
    // caption reflects the new name after a rename.
    waiver.versionLabel = this.generateVersionLabel(trimmed, waiver.version, now);
    this.savedWaivers$.next([...this.savedWaivers]);
    this.emitEffectiveGroups();
  }

  /**
   * Moves a waiver into an explicit group. If the waiver is currently
   * in a different group, it's first removed from that one (transaction-
   * ally so the invariants — every waiver in exactly zero or one group —
   * hold at commit time).
   */
  async addWaiverToGroup(groupId: string, waiverId: string): Promise<void> {
    const group = this.waiverGroups.find(g => g.id === groupId);
    const waiver = this.savedWaivers.find(w => w.id === waiverId);
    if (!group || !waiver || !waiver.id) throw new Error('Group or waiver not found.');
    if (group.waiverIds.includes(waiverId)) return;
    // Cap enforced at the write layer so any add path (Add Variant
    // button, drag-into-group, future imports) hits the same limit.
    // The waiver-list UI also disables the trigger at the cap, so
    // callers should treat this throw as a defensive last line rather
    // than a routine control-flow error.
    if (group.waiverIds.length >= MAX_VARIANTS_PER_GROUP) {
      throw new Error(`A waiver group can hold at most ${MAX_VARIANTS_PER_GROUP} variants.`);
    }

    const now = new Date();
    const batch = this.afs.firestore.batch();

    // If the waiver already sits in another explicit group, splice it
    // out in the same batch. If that removes the source group's LAST
    // member, delete the source group doc in the same batch so we
    // don't leave an empty shell (which would otherwise render as a
    // phantom row — `getEffectiveGroups` filters those out defensively
    // but the doc still bloats Firestore over time).
    let sourceGroup: BuilderWaiverGroup | undefined;
    let sourceEmptiedOut = false;
    if (waiver.groupId && waiver.groupId !== groupId) {
      sourceGroup = this.waiverGroups.find(g => g.id === waiver.groupId);
      if (sourceGroup) {
        const remaining = sourceGroup.waiverIds.filter(id => id !== waiverId);
        if (remaining.length === 0) {
          sourceEmptiedOut = true;
          batch.delete(this.afs.collection('builderWaiverGroups').doc(sourceGroup.id).ref);
        } else {
          const sourcePatch: Record<string, unknown> = {
            waiverIds: remaining,
            updatedAt: now,
          };
          if (sourceGroup.activeWaiverId === waiverId && remaining[0]) {
            sourcePatch['activeWaiverId'] = remaining[0];
          }
          if (sourceGroup.minorWaiverId === waiverId) {
            sourcePatch['minorWaiverId'] = firebase.firestore.FieldValue.delete();
          }
          batch.update(this.afs.collection('builderWaiverGroups').doc(sourceGroup.id).ref, sourcePatch);
        }
      }
    }

    const nextIds = [...group.waiverIds, waiverId];
    batch.update(this.afs.collection('builderWaiverGroups').doc(groupId).ref, {
      waiverIds: nextIds,
      updatedAt: now,
    });
    batch.update(this.afs.collection('builderWaivers').doc(waiverId).ref, {
      groupId,
      updatedAt: now,
    });

    await batch.commit();

    // Cache updates mirror the batch.
    if (sourceGroup) {
      if (sourceEmptiedOut) {
        this.waiverGroups = this.waiverGroups.filter(g => g.id !== sourceGroup!.id);
      } else {
        const remaining = sourceGroup.waiverIds.filter(id => id !== waiverId);
        sourceGroup.waiverIds = remaining;
        if (sourceGroup.activeWaiverId === waiverId && remaining[0]) sourceGroup.activeWaiverId = remaining[0];
        if (sourceGroup.minorWaiverId === waiverId) delete sourceGroup.minorWaiverId;
        sourceGroup.updatedAt = now;
      }
    }
    group.waiverIds = nextIds;
    group.updatedAt = now;
    waiver.groupId = groupId;
    this.emitEffectiveGroups();
    this.savedWaivers$.next([...this.savedWaivers]);
  }

  /**
   * Kicks a waiver out of its group.
   *
   * The removed variant lands in a **new explicit solo group** whose
   * id equals the waiver's own id — same pattern the auto-migration
   * uses for pre-group waivers. Doing this (rather than leaving it as
   * an implicit group with a cleared `groupId`) means the sidebar
   * still renders rename/delete-group affordances for the removed
   * row; the previous behavior stranded it as a read-only implicit
   * row.
   *
   * If the removed variant was the last member of its group, that
   * group's doc is deleted (via `deleteGroup`, which itself creates
   * per-variant solo groups for anyone still inside — see below).
   * Otherwise the source group's active/minor pointers advance to a
   * remaining sibling as needed.
   */
  async removeWaiverFromGroup(groupId: string, waiverId: string): Promise<void> {
    const group = this.waiverGroups.find(g => g.id === groupId);
    const waiver = this.savedWaivers.find(w => w.id === waiverId);
    if (!group || !waiver || !waiver.id) return;
    if (!group.waiverIds.includes(waiverId)) return;

    const remaining = group.waiverIds.filter(id => id !== waiverId);
    if (remaining.length === 0) {
      // Last variant leaving — delete the group doc.
      await this.deleteGroup(groupId);
      return;
    }

    const companyId = this.currentUserService.currentUser?.companyId;
    if (!companyId) throw new Error('No active company; cannot remove variant from group.');

    const now = new Date();
    const batch = this.afs.firestore.batch();

    // Source group — splice the leaving variant out, advance active /
    // clear minor as needed.
    const patch: Record<string, unknown> = { waiverIds: remaining, updatedAt: now };
    if (group.activeWaiverId === waiverId) patch['activeWaiverId'] = remaining[0];
    if (group.minorWaiverId === waiverId) patch['minorWaiverId'] = firebase.firestore.FieldValue.delete();
    batch.update(this.afs.collection('builderWaiverGroups').doc(groupId).ref, patch);

    // New solo group for the removed variant, at `builderWaiverGroups/{waiverId}`.
    // Using the waiver's own id keeps the auto-migration invariant
    // (every waiver in a group whose id == its own id when solo) and
    // means any existing `products.waivers[]` refs still resolve.
    // set() is used unconditionally — if a leftover empty group happens
    // to already exist at this id (from prior moves), overwriting with
    // this correct solo shape is safe.
    batch.set(this.afs.collection('builderWaiverGroups').doc(waiverId).ref, {
      companyId,
      name: waiver.name || 'Untitled Group',
      waiverIds: [waiverId],
      activeWaiverId: waiverId,
      createdAt: now,
      updatedAt: now,
    });
    batch.update(this.afs.collection('builderWaivers').doc(waiverId).ref, {
      groupId: waiverId,
      updatedAt: now,
    });

    await batch.commit();

    // Cache: source group state.
    group.waiverIds = remaining;
    if (group.activeWaiverId === waiverId) group.activeWaiverId = remaining[0];
    if (group.minorWaiverId === waiverId) delete group.minorWaiverId;
    group.updatedAt = now;
    // Cache: replace (or add) the removed variant's solo group entry.
    const existingSoloIdx = this.waiverGroups.findIndex(g => g.id === waiverId);
    const soloGroup: BuilderWaiverGroup = {
      id: waiverId,
      companyId,
      name: waiver.name || 'Untitled Group',
      waiverIds: [waiverId],
      activeWaiverId: waiverId,
      createdAt: now,
      updatedAt: now,
    };
    if (existingSoloIdx > -1) this.waiverGroups[existingSoloIdx] = soloGroup;
    else this.waiverGroups.push(soloGroup);
    waiver.groupId = waiverId;
    this.emitEffectiveGroups();
    this.savedWaivers$.next([...this.savedWaivers]);
  }

  /** Sets which variant in a group renders for adult participants. Must
   *  be a member of the group already; the UI enforces this. */
  async setActiveWaiver(groupId: string, waiverId: string): Promise<void> {
    const group = this.waiverGroups.find(g => g.id === groupId);
    if (!group) return;
    if (!group.waiverIds.includes(waiverId)) throw new Error('Waiver is not a member of this group.');
    if (group.activeWaiverId === waiverId) return;
    const now = new Date();
    await this.afs.collection('builderWaiverGroups').doc(groupId).update({
      activeWaiverId: waiverId,
      updatedAt: now,
    });
    group.activeWaiverId = waiverId;
    group.updatedAt = now;
    this.emitEffectiveGroups();
  }

  /**
   * Flags the group's minor-participant variant. Pass `null` to clear
   * it — minors then fall back to the active variant (whose own guardian
   * fields carry the minor case if present).
   */
  async setMinorWaiver(groupId: string, waiverId: string | null): Promise<void> {
    const group = this.waiverGroups.find(g => g.id === groupId);
    if (!group) return;
    if (waiverId && !group.waiverIds.includes(waiverId)) {
      throw new Error('Waiver is not a member of this group.');
    }
    if (group.minorWaiverId === (waiverId ?? undefined)) return;
    const now = new Date();
    const patch: Record<string, unknown> = { updatedAt: now };
    if (waiverId) patch['minorWaiverId'] = waiverId;
    else patch['minorWaiverId'] = firebase.firestore.FieldValue.delete();
    await this.afs.collection('builderWaiverGroups').doc(groupId).update(patch);
    if (waiverId) group.minorWaiverId = waiverId;
    else delete group.minorWaiverId;
    group.updatedAt = now;
    this.emitEffectiveGroups();
  }

  /**
   * Deletes an explicit group.
   *
   * Semantics differ by group size:
   *   - **Solo group** (single variant): the group AND its lone
   *     waiver are deleted together. From the user's perspective, a
   *     solo group and its only variant are the same thing — leaving
   *     the waiver around would immediately re-render as an implicit
   *     group in the sidebar, and the auto-migration on next load
   *     would re-create the very group they just deleted.
   *   - **Multi-variant group**: only the group doc is deleted. Each
   *     surviving variant's `groupId` is cleared and it reverts to an
   *     implicit solo group. Waiver docs are preserved so a partner
   *     who accidentally hit "delete group" doesn't lose actual
   *     signed-template content.
   *
   * A solo group's waiver is archived first ({@link archiveBeforeDelete}),
   * exactly as {@link deleteWaiver} does; if that fails nothing is deleted.
   */
  async deleteGroup(groupId: string): Promise<void> {
    const group = this.waiverGroups.find(g => g.id === groupId);
    if (!group) return;
    const isSolo = group.waiverIds.length <= 1;
    const now = new Date();
    const archived = isSolo ? await this.archiveBeforeDelete(group.waiverIds) : new Map<string, number | undefined>();
    await this.afs.firestore.runTransaction(async (tx) => {
      // Reads first (transaction rule): every waiver about to be deleted
      // must still be the version just archived.
      for (const [wId, version] of archived) await this.assertArchivedInTransaction(tx, wId, version);

      tx.delete(this.afs.collection('builderWaiverGroups').doc(groupId).ref);
      for (const wId of group.waiverIds) {
        if (isSolo) {
          // Solo group — delete the underlying waiver in the same write
          // so the row is gone for good (no implicit re-render, no auto-
          // migration resurrection on next load).
          tx.delete(this.afs.collection('builderWaivers').doc(wId).ref);
        } else {
          tx.update(this.afs.collection('builderWaivers').doc(wId).ref, {
            groupId: firebase.firestore.FieldValue.delete(),
            updatedAt: now,
          });
        }
      }
    });

    this.waiverGroups = this.waiverGroups.filter(g => g.id !== groupId);
    if (isSolo) {
      // Drop deleted waivers from the local cache too.
      this.savedWaivers = this.savedWaivers.filter(w => !group.waiverIds.includes(w.id ?? ''));
    } else {
      for (const w of this.savedWaivers) {
        if (group.waiverIds.includes(w.id ?? '')) w.groupId = undefined;
      }
    }
    this.emitEffectiveGroups();
    this.savedWaivers$.next([...this.savedWaivers]);
  }

  private makeImplicitGroup(waiver: WaiverFormConfig): BuilderWaiverGroup {
    // `companyId` is left blank because the implicit group is a view-
    // level construct and never persisted; callers that need company
    // scope read from the underlying waiver instead.
    return {
      id: waiver.id ?? '',
      companyId: '',
      name: waiver.name || 'Untitled Waiver',
      waiverIds: waiver.id ? [waiver.id] : [],
      activeWaiverId: waiver.id ?? '',
      createdAt: waiver.createdAt,
      updatedAt: waiver.updatedAt,
    };
  }

  private emitEffectiveGroups(): void {
    this.waiverGroups$.next(this.getEffectiveGroups());
  }

  /** Normalize a Firestore group doc into a BuilderWaiverGroup. */
  private hydrateGroup(id: string, raw: Record<string, unknown>): BuilderWaiverGroup {
    const toDate = (v: unknown): Date | undefined => {
      if (!v) return undefined;
      if (v instanceof Date) return v;
      if (typeof v === 'object' && v !== null && typeof (v as { toDate?: () => Date }).toDate === 'function') {
        return (v as { toDate: () => Date }).toDate();
      }
      if (typeof v === 'string' || typeof v === 'number') return new Date(v);
      return undefined;
    };
    const waiverIds = Array.isArray(raw['waiverIds'])
      ? (raw['waiverIds'] as unknown[]).filter((x): x is string => typeof x === 'string')
      : [];
    const activeWaiverId = typeof raw['activeWaiverId'] === 'string' ? (raw['activeWaiverId'] as string) : (waiverIds[0] ?? '');
    return {
      id,
      companyId: typeof raw['companyId'] === 'string' ? (raw['companyId'] as string) : '',
      name: typeof raw['name'] === 'string' ? (raw['name'] as string) : 'Untitled Group',
      waiverIds,
      activeWaiverId,
      ...(typeof raw['minorWaiverId'] === 'string' && (raw['minorWaiverId'] as string)
        ? { minorWaiverId: raw['minorWaiverId'] as string }
        : {}),
      createdAt: toDate(raw['createdAt']),
      updatedAt: toDate(raw['updatedAt']),
    };
  }

  /**
   * Returns the company's designated Minor Waiver, or undefined if none.
   * Prefers the in-memory cache (populated by {@link loadCompanyWaivers});
   * a cache miss falls back to a single-shot Firestore query so callers
   * on paths that don't hydrate the cache (public envelope-sign flow)
   * still work. Only one waiver per company should ever match — enforced
   * by {@link saveWaiver}. If the invariant is violated (rare — manual
   * Firestore edits), we return the first match.
   */
  async getMinorWaiverForCompany(companyId: string): Promise<WaiverFormConfig | undefined> {
    if (!companyId) return undefined;

    if (this.loaded && this.loadedCompanyId === companyId) {
      return this.savedWaivers.find(w => w.isMinorWaiver);
    }

    // The saveWaiver batch enforces "at most one Minor Waiver per
    // company"; the only way this query returns more than one is a
    // manual Firestore edit. `limit(1)` accepts the non-deterministic
    // winner in that case — adding `orderBy('updatedAt', 'desc')` would
    // require a composite index (`companyId + isMinorWaiver + updatedAt`)
    // that isn't worth carrying just for a manual-repair edge case.
    const snap = await this.afs
      .collection('builderWaivers').ref
      .where('companyId', '==', companyId)
      .where('isMinorWaiver', '==', true)
      .limit(1)
      .get();
    if (snap.empty) return undefined;
    const doc = snap.docs[0];
    return this.hydrateWaiver(doc.id, doc.data());
  }

  /**
   * Deletes a builder waiver. Its current version is archived to
   * `/versions/{N}` first ({@link archiveBeforeDelete}): the current version
   * otherwise only lives on the parent doc, so deleting it would lose the
   * template behind every signature pinned to it. If archiving fails,
   * nothing is deleted.
   */
  async deleteWaiver(id: string): Promise<void> {
    // Clean up every group ref that mentions this waiver — otherwise
    // the group's `waiverIds` keeps a dangling id (so the sidebar's
    // "N variants" count reads too high but only N-1 rows render) and
    // `activeWaiverId` / `minorWaiverId` end up pointing at a ghost.
    // Written together with the waiver delete so callers see a
    // consistent snapshot the next time they read.
    const now = new Date();
    const groupWrites: Array<{ ref: firebase.firestore.DocumentReference; patch?: Record<string, unknown> }> = [];
    const affectedGroups: Array<{
      group: BuilderWaiverGroup;
      remaining: string[];
      shouldDelete: boolean;
    }> = [];

    for (const group of this.waiverGroups) {
      if (!group.waiverIds.includes(id)) continue;
      const remaining = group.waiverIds.filter(x => x !== id);
      if (remaining.length === 0) {
        // Removing the waiver empties the group — delete the group
        // doc rather than leaving an empty shell in Firestore.
        groupWrites.push({ ref: this.afs.collection('builderWaiverGroups').doc(group.id).ref });
        affectedGroups.push({ group, remaining, shouldDelete: true });
      } else {
        const patch: Record<string, unknown> = {
          waiverIds: remaining,
          updatedAt: now,
        };
        if (group.activeWaiverId === id) patch['activeWaiverId'] = remaining[0];
        if (group.minorWaiverId === id) patch['minorWaiverId'] = firebase.firestore.FieldValue.delete();
        groupWrites.push({ ref: this.afs.collection('builderWaiverGroups').doc(group.id).ref, patch });
        affectedGroups.push({ group, remaining, shouldDelete: false });
      }
    }

    const archived = await this.archiveBeforeDelete([id]);
    await this.afs.firestore.runTransaction(async (tx) => {
      await this.assertArchivedInTransaction(tx, id, archived.get(id));
      for (const write of groupWrites) {
        if (write.patch) tx.update(write.ref, write.patch);
        else tx.delete(write.ref);
      }
      tx.delete(this.afs.collection('builderWaivers').doc(id).ref);
    });

    // Mirror the batch in the local caches.
    for (const { group, remaining, shouldDelete } of affectedGroups) {
      if (shouldDelete) {
        this.waiverGroups = this.waiverGroups.filter(g => g.id !== group.id);
      } else {
        group.waiverIds = remaining;
        if (group.activeWaiverId === id) group.activeWaiverId = remaining[0];
        if (group.minorWaiverId === id) delete group.minorWaiverId;
        group.updatedAt = now;
      }
    }
    this.savedWaivers = this.savedWaivers.filter(w => w.id !== id);
    this.savedWaivers$.next([...this.savedWaivers]);
    this.emitEffectiveGroups();
  }

  /**
   * Archives the current version of each waiver about to be deleted, via
   * {@link archiveCurrentVersion}: a snapshot that already exists (a
   * retried delete, or a save that archived it) is kept, never rewritten.
   * Throws — so the caller deletes nothing — if any archive fails.
   *
   * @returns The archived version per waiver id; undefined when the waiver
   *   doc is already gone, so there is nothing left to preserve.
   */
  private async archiveBeforeDelete(ids: string[]): Promise<Map<string, number | undefined>> {
    const archived = new Map<string, number | undefined>();
    for (const id of ids) {
      if (!id) continue;
      const { archivedVersion } = await this.archiveCurrentVersion(id);
      archived.set(id, archivedVersion);
    }
    return archived;
  }

  /**
   * Inside the delete transaction: refuse when the waiver is no longer the
   * version {@link archiveBeforeDelete} just archived (a save landed in
   * between) or that snapshot is missing, so a version is never deleted
   * unarchived. A waiver that is already gone needs nothing.
   */
  private async assertArchivedInTransaction(
    tx: firebase.firestore.Transaction,
    waiverId: string,
    archivedVersion: number | undefined,
  ): Promise<void> {
    const parentRef = this.afs.collection('builderWaivers').doc(waiverId).ref;
    const parent = await tx.get(parentRef);
    if (!parent.exists) return;
    const data = (parent.data() ?? {}) as Record<string, unknown>;
    const current = typeof data['version'] === 'number' && (data['version'] as number) > 0
      ? (data['version'] as number)
      : 1;
    const snapshot = archivedVersion === current
      ? await tx.get(parentRef.collection('versions').doc(String(current)))
      : null;
    if (!snapshot?.exists) {
      throw new Error('This waiver changed while it was being deleted. Please try again.');
    }
  }

  /**
   * Synchronous cache-only lookup. Returns `undefined` when the waiver isn't
   * in cache — callers in anonymous flows (the public sign page) must use
   * {@link getWaiverByIdAsync} instead, since their cache is never hydrated.
   */
  getWaiverById(id: string): WaiverFormConfig | undefined {
    const waiver = this.savedWaivers.find(w => w.id === id);
    return waiver ? { ...waiver, fields: [...waiver.fields] } : undefined;
  }

  /**
   * Async lookup that falls back to Firestore on cache miss. Used by the
   * public `/sign-waiver/:id` route where there's no logged-in partner and
   * therefore no pre-loaded cache.
   *
   * @param forceFresh Skip the in-memory cache and read directly from
   *   Firestore. Used by the signed-waiver viewer so a partner opening
   *   a signed waiver always sees the current template, not whatever
   *   was cached when the modal was first mounted.
   */
  async getWaiverByIdAsync(id: string, forceFresh = false): Promise<WaiverFormConfig | undefined> {
    if (!forceFresh) {
      const cached = this.getWaiverById(id);
      if (cached) return cached;
    }

    const snap = await this.afs.collection('builderWaivers').doc(id).ref.get();
    if (!snap.exists) return undefined;
    const hydrated = this.hydrateWaiver(snap.id, snap.data());

    // Keep the cache in step with what we just read — otherwise the next
    // non-forceFresh caller (e.g. the rental modal list) would still see
    // the stale entry.
    if (forceFresh && hydrated) {
      const idx = this.savedWaivers.findIndex(w => w.id === id);
      if (idx > -1) this.savedWaivers[idx] = hydrated;
      else this.savedWaivers.push(hydrated);
      this.savedWaivers$.next([...this.savedWaivers]);
    }
    return hydrated;
  }

  private loaded = false;
  private loadedCompanyId: string | null = null;

  /** Normalize a Firestore doc into a WaiverFormConfig (Timestamp → Date). */
  private hydrateWaiver(id: string, raw: any): WaiverFormConfig {
    const toDate = (v: any) => {
      if (!v) return undefined;
      if (v instanceof Date) return v;
      if (typeof v.toDate === 'function') return v.toDate();
      if (typeof v === 'string' || typeof v === 'number') return new Date(v);
      return undefined;
    };
    // Drop any legacy `type: 'pdf'` field entries — that field type was
    // removed but old docs may still carry it. Leaving them in would break
    // typechecking downstream and render as empty rows.
    const rawFields = Array.isArray(raw?.fields) ? raw.fields : [];
    const fields = rawFields.filter((f: { type?: string }) => f?.type !== 'pdf');

    return {
      id,
      ...(typeof raw?.companyId === 'string' && raw.companyId ? { companyId: raw.companyId } : {}),
      name: raw?.name ?? '',
      version: raw?.version ?? 1,
      versionLabel: raw?.versionLabel ?? '',
      fields,
      createdAt: toDate(raw?.createdAt),
      updatedAt: toDate(raw?.updatedAt),
      ...(toDate(raw?.publishedAt) ? { publishedAt: toDate(raw?.publishedAt) } : {}),
      // Back-pointer to the waiver's group. Dropping it made
      // `resolveWaiverForParticipant`'s first branch dead, so every minor
      // on a migrated group silently received the ADULT variant —
      // `pickWaiverVariant`, the only reader of `minorWaiverId`, was never
      // reached.
      ...(typeof raw?.groupId === 'string' && raw.groupId ? { groupId: raw.groupId } : {}),
      // Waiver Kiosk visibility; absent means shown on the kiosk page.
      ...(raw?.hiddenFromKiosk === true ? { hiddenFromKiosk: true } : {}),
      // Minor-Waiver + age-threshold fields are optional; leave undefined
      // when absent so `?? DEFAULT_MINOR_AGE_THRESHOLD` reads cleanly at
      // the render sites and the `isMinorWaiver` flag stays falsy.
      ...(raw?.isMinorWaiver ? { isMinorWaiver: true } : {}),
      ...(typeof raw?.minorAgeThreshold === 'number' ? { minorAgeThreshold: raw.minorAgeThreshold } : {}),
      // Attached template PDF URL, if any. Absent for waivers that don't
      // use a PDF template (form-only) — the sign surfaces treat missing
      // `pdfContent` as "no iframe to render".
      ...(typeof raw?.pdfContent === 'string' && raw.pdfContent
        ? { pdfContent: raw.pdfContent }
        : {}),
    };
  }

  async getProductAssignments(waiverId: string): Promise<ProductWaiverAssignment[]> {
    const products: ProductV1[] = await this.productsService.getActiveProductsForCompanyPromise();
    return products.map((product) => ({
      productId: product.id,
      productNumber: product.productNumber || '',
      productName: product.productName,
      productCategory: product.productCategory || '',
      selected: this.getEffectiveWaivers(product).includes(waiverId),
    }));
  }

  async saveProductAssignments(waiverId: string, assignments: ProductWaiverAssignment[]): Promise<void> {
    const products: ProductV1[] = await this.productsService.getActiveProductsForCompanyPromise();

    for (const assignment of assignments) {
      const product = products.find((p) => p.id === assignment.productId);
      if (!product) continue;

      const waivers = this.getEffectiveWaivers(product);
      const hasWaiver = waivers.includes(waiverId);

      if (assignment.selected && !hasWaiver) {
        waivers.push(waiverId);
        await this.updateProductWaivers(product.id, waivers);
      } else if (!assignment.selected && hasWaiver) {
        const filtered = waivers.filter(id => id !== waiverId);
        await this.updateProductWaivers(product.id, filtered);
      }
    }
  }

  /**
   * Returns one attachment row per active product group for the current
   * company, with a product count for context in the modal. Selection is
   * derived UNION-STYLE: a group is "selected" if either
   *
   *   a) the group's own `waivers[]` array contains {@link waiverId}, OR
   *   b) any active product whose `productGroupID` matches the group has
   *      {@link waiverId} in its own per-product `waivers[]` array.
   *
   * (b) exists so legacy per-product attachments (which pre-date the
   * group-level model) surface as selected instead of falsely reading as
   * detached. {@link saveProductGroupAssignments} then promotes them to
   * group-level on the next save, so the data self-heals whenever a
   * partner touches the modal.
   */
  async getProductGroupAssignments(waiverId: string): Promise<ProductGroupWaiverAssignment[]> {
    const companyId = this.currentUserService.currentUser?.companyId;
    if (!companyId) return [];

    // Load groups and products in parallel — the product count per group
    // is a simple bucket over the products list, no additional query
    // needed. Products carry `waivers` too so we can compute the legacy
    // per-product union in the same pass.
    type GroupRow = { id: string; groupName?: string; categoryName?: string | null; waivers?: string[] };
    const groupsPromise = this.firestoreService.getCollectionPromise(Collection.ProductGroup, [
      { field: 'companyID', operator: '==', value: companyId },
      { field: 'isActive', operator: '==', value: true },
    ], []) as Promise<GroupRow[]>;
    const [groups, products] = await Promise.all([
      groupsPromise,
      this.productsService.getActiveProductsForCompanyPromise(),
    ]);

    const countByGroup = new Map<string, number>();
    const productWaiverGroupIds = new Set<string>();
    for (const p of products) {
      const gid = (p as { productGroupID?: string }).productGroupID;
      if (!gid) continue;
      countByGroup.set(gid, (countByGroup.get(gid) ?? 0) + 1);
      const productWaivers = (p as { waivers?: string[] }).waivers;
      if (Array.isArray(productWaivers) && productWaivers.includes(waiverId)) {
        productWaiverGroupIds.add(gid);
      }
    }

    return groups.map((group: GroupRow) => ({
      groupId: group.id,
      groupName: group.groupName || 'Unnamed Group',
      categoryName: group.categoryName || '',
      productCount: countByGroup.get(group.id) ?? 0,
      selected:
        (Array.isArray(group.waivers) && group.waivers.includes(waiverId)) ||
        productWaiverGroupIds.has(group.id),
    }));
  }

  /**
   * Persists the toggles from {@link getProductGroupAssignments} back to
   * `productGroup/{groupId}.waivers[]` and completes the legacy-cleanup
   * pass in the same operation.
   *
   * For every SELECTED group:
   *   - Ensure `group.waivers[]` contains {@link waiverId}.
   *   - Strip {@link waiverId} from every active product in that group's
   *     `product.waivers[]` array. Under the group-level model the group
   *     is the sole source of truth; leaving per-product entries in place
   *     would leak duplicates back through the CF's UNION read.
   *
   * For every UNSELECTED group:
   *   - Ensure `group.waivers[]` does NOT contain {@link waiverId}.
   *   - Also strip {@link waiverId} from every product in the group.
   *     Otherwise a partner explicitly toggling a group OFF would see
   *     the waiver resurrected on the next load via the union check.
   *
   * Idempotent — groups/products already in the desired shape aren't
   * rewritten, so the common case (partner opens modal, hits Save
   * without changes) is a no-op.
   */
  async saveProductGroupAssignments(
    waiverId: string,
    assignments: ProductGroupWaiverAssignment[],
  ): Promise<void> {
    const companyId = this.currentUserService.currentUser?.companyId;
    if (!companyId) throw new Error('No active company; cannot save product-group assignments.');
    if (!waiverId) throw new Error('waiverId is required.');
    if (!assignments.length) return;

    // Read phase — kick off the product-list fetch and one .get() per
    // group in parallel. The previous implementation awaited each group
    // .get() inside the write loop, which turned a modal save into
    // dozens of serial Firestore round-trips (5-10s on companies with
    // many groups). Fanned-out reads collapse to a single wall-clock
    // round-trip. The .get() also tells us whether the group still
    // exists — a batch.update() against a missing doc would throw and
    // fail the entire commit.
    const [products, ...groupSnaps] = await Promise.all([
      this.productsService.getActiveProductsForCompanyPromise(),
      ...assignments.map(a =>
        this.afs.collection('productGroup').doc(a.groupId).ref.get().catch(() => null),
      ),
    ]);

    const productsByGroup = new Map<string, ProductV1[]>();
    for (const p of products) {
      const gid = (p as { productGroupID?: string }).productGroupID;
      if (!gid) continue;
      const bucket = productsByGroup.get(gid) ?? [];
      bucket.push(p as ProductV1);
      productsByGroup.set(gid, bucket);
    }

    // Compute the diff in memory so we still skip no-op writes (partner
    // opens the modal, hits Save without changes → 0 ops). Then queue
    // every real change as a batch op with idempotent arrayUnion /
    // arrayRemove — the values are safe under a concurrent write from
    // another tab.
    type Op = { ref: firebase.firestore.DocumentReference; data: Record<string, unknown> };
    const ops: Op[] = [];

    for (let i = 0; i < assignments.length; i++) {
      const assignment = assignments[i];
      const snap = groupSnaps[i];
      if (!snap || !snap.exists) continue;

      const data = snap.data() as { waivers?: string[] } | undefined;
      const current = Array.isArray(data?.waivers) ? (data as { waivers: string[] }).waivers : [];
      const has = current.includes(waiverId);
      const groupRef = this.afs.collection('productGroup').doc(assignment.groupId).ref;

      if (assignment.selected && !has) {
        ops.push({
          ref: groupRef,
          data: { waivers: firebase.firestore.FieldValue.arrayUnion(waiverId) },
        });
      } else if (!assignment.selected && has) {
        ops.push({
          ref: groupRef,
          data: { waivers: firebase.firestore.FieldValue.arrayRemove(waiverId) },
        });
      }

      // Sweep per-product legacy entries in either direction. Selected +
      // per-product entry = redundant duplicate; unselected + per-product
      // entry = resurrection risk on next load. Both call for a clean.
      // Filtered by .includes() so we don't burn a write on products
      // that never carried this waiverId.
      const groupProducts = productsByGroup.get(assignment.groupId) ?? [];
      for (const product of groupProducts) {
        if (!this.getEffectiveWaivers(product).includes(waiverId)) continue;
        ops.push({
          ref: this.afs.collection('products').doc(product.id).ref,
          data: { waivers: firebase.firestore.FieldValue.arrayRemove(waiverId) },
        });
      }
    }

    // Commit in one batch when we can (Firestore caps at 500 writes per
    // batch). Chunked at 450 so an add-a-few-ops future edit still fits.
    // A pathological company (100+ groups × 10 products) rolls into
    // multiple batches but each is still one round-trip.
    const CHUNK = 450;
    for (let i = 0; i < ops.length; i += CHUNK) {
      const batch = this.afs.firestore.batch();
      for (const op of ops.slice(i, i + CHUNK)) {
        batch.update(op.ref, op.data);
      }
      await batch.commit();
    }
  }

  createField(type: WaiverFieldType, order: number): WaiverField {
    const template = WAIVER_FIELD_TEMPLATES.find(t => t.type === type);

    return {
      id: this.generateId(),
      type,
      label: template?.label || type,
      required: true,
      order,
      placeholder: template?.placeholder || '',
    };
  }

  createGuardianNameField(order: number): WaiverField {
    return {
      id: this.generateId(),
      type: 'guardianName',
      label: 'Parent/Guardian Name',
      required: true,
      order,
      placeholder: 'Enter parent or guardian full name',
    };
  }

  createGuardianSignatureField(order: number): WaiverField {
    return {
      id: this.generateId(),
      type: 'guardianSignature',
      label: 'Parent/Guardian Signature',
      required: true,
      order,
    };
  }

  reorderFields(fields: WaiverField[]): WaiverField[] {
    return fields.map((field, index) => ({
      ...field,
      order: index
    }));
  }

  /** Whether a waiver's fields let a parent/guardian sign it (both halves present). */
  hasGuardianPair(fields: ReadonlyArray<WaiverField>): boolean {
    return fields.some(f => f.type === 'guardianName') && fields.some(f => f.type === 'guardianSignature');
  }

  /**
   * The guardian pair a MINOR waiver needs to be signable online (a minor's
   * waiver without both is "Can't be signed online" — see
   * `waiverSigningReadiness`). Adds only the half (or halves) missing,
   * immediately before the first participant `signature` field, or at the
   * end when there is none; never duplicates one already present and leaves
   * every other field in its order. The added fields are required, as the
   * factories make them.
   *
   * Pure: returns a new, renumbered list and the fields it added; the input
   * is not modified.
   */
  ensureGuardianPair(fields: ReadonlyArray<WaiverField>): { fields: WaiverField[]; added: WaiverField[] } {
    const added: WaiverField[] = [];
    if (!fields.some(f => f.type === 'guardianName')) added.push(this.createGuardianNameField(0));
    if (!fields.some(f => f.type === 'guardianSignature')) added.push(this.createGuardianSignatureField(0));
    if (added.length === 0) return { fields: [...fields], added };

    const next = [...fields];
    const signatureIndex = next.findIndex(f => f.type === 'signature');
    next.splice(signatureIndex === -1 ? next.length : signatureIndex, 0, ...added);
    const renumbered = this.reorderFields(next);
    const addedIds = new Set(added.map(f => f.id));
    return { fields: renumbered, added: renumbered.filter(f => addedIds.has(f.id)) };
  }

  buildDefaultFormConfig(): WaiverFormConfig {
    const now = new Date();
    return {
      name: '',
      version: 1,
      versionLabel: this.generateVersionLabel('', 1, now),
      fields: [],
      createdAt: now,
      updatedAt: now,
    };
  }

  generateVersionLabel(name: string, version: number, date: Date): string {
    const slug = this.slugify(name);
    const dateStr = this.formatDate(date);
    return slug ? `${slug}-version-${version}.${dateStr}` : `waiver-version-${version}.${dateStr}`;
  }

  incrementVersion(config: WaiverFormConfig): WaiverFormConfig {
    const newVersion = config.version + 1;
    const now = new Date();
    return {
      ...config,
      version: newVersion,
      versionLabel: this.generateVersionLabel(config.name, newVersion, now),
      updatedAt: now,
    };
  }

  private slugify(name: string): string {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  private formatDate(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}${m}${d}`;
  }

  validateFormConfig(config: WaiverFormConfig): string[] {
    const errors: string[] = [];

    if (!config.name || config.name.trim().length === 0) {
      errors.push('Waiver form name is required.');
    }

    if (config.fields.length === 0) {
      errors.push('At least one field is required.');
    }

    const hasSignature = config.fields.some(f => f.type === 'signature');
    const hasGuardianName = config.fields.some(f => f.type === 'guardianName');
    const hasGuardianSig = config.fields.some(f => f.type === 'guardianSignature');

    // Either an adult signature OR a guardian signature satisfies the
    // "at least one signature" requirement — on a minor-only waiver the
    // guardian IS the signer, so demanding a separate adult signature
    // field as well would be wrong.
    if (!hasSignature && !hasGuardianSig) {
      errors.push('A signature field is required.');
    }
    // The guardian pair has to stay whole: a signature with nobody named,
    // or a name with nowhere to sign, captures nothing usable. The builder
    // inserts and removes them together, so this only catches hand-edited
    // or legacy docs.
    if (hasGuardianSig && !hasGuardianName) {
      errors.push('A Parent/Guardian Signature field requires a Parent/Guardian Name field.');
    }
    if (hasGuardianName && !hasGuardianSig) {
      errors.push('A Parent/Guardian Name field requires a Parent/Guardian Signature field.');
    }

    return errors;
  }

  /**
   * Get products that have a specific waiver attached.
   */
  async getAffectedProducts(waiverId: string): Promise<ReplacementProductAssignment[]> {
    const products: ProductV1[] = await this.productsService.getActiveProductsForCompanyPromise();
    return products
      .filter(p => this.getEffectiveWaivers(p).includes(waiverId))
      .map(p => ({
        productId: p.id,
        productNumber: p.productNumber || '',
        productName: p.productName,
        productCategory: p.productCategory || '',
        replacementWaiverId: '',
      }));
  }

  /**
   * Replace a deleted waiver with replacement waivers on affected products.
   * Each product can have a different replacement waiver (or none if empty string).
   */
  async replaceWaiverOnProducts(deletedWaiverId: string, replacements: ReplacementProductAssignment[]): Promise<void> {
    const products: ProductV1[] = await this.productsService.getActiveProductsForCompanyPromise();

    for (const replacement of replacements) {
      const product = products.find(p => p.id === replacement.productId);
      if (!product) continue;

      let waivers = this.getEffectiveWaivers(product);
      // Remove the deleted waiver
      waivers = waivers.filter(id => id !== deletedWaiverId);
      // Add replacement if one was selected and not already present
      if (replacement.replacementWaiverId && !waivers.includes(replacement.replacementWaiverId)) {
        waivers.push(replacement.replacementWaiverId);
      }
      await this.updateProductWaivers(product.id, waivers);
    }
  }

  /**
   * Generate a shareable signing link for a waiver.
   * Encodes waiver ID + optional customer prefill data as base64 query param for a shorter URL.
   */
  generateSigningLink(waiverId: string, prefill?: { name?: string; email?: string; phone?: string }): string {
    const origin = window.location.origin;
    let url = `${origin}/sign-waiver/${waiverId}`;

    const params: string[] = [];
    if (prefill?.name) params.push(`name=${encodeURIComponent(prefill.name)}`);
    if (prefill?.email) params.push(`email=${encodeURIComponent(prefill.email)}`);
    if (prefill?.phone) params.push(`phone=${encodeURIComponent(prefill.phone)}`);

    if (params.length > 0) {
      url += '?' + params.join('&');
    }

    return url;
  }

  /**
   * Generate a short-style signing link using base64-encoded payload.
   * Format: /w/<base64 payload>
   */
  generateShortSigningLink(waiverId: string, prefill?: { name?: string; email?: string; phone?: string }): string {
    const origin = window.location.origin;
    const payload: { id: string; n?: string; e?: string; p?: string } = { id: waiverId };
    if (prefill?.name) payload.n = prefill.name;
    if (prefill?.email) payload.e = prefill.email;
    if (prefill?.phone) payload.p = prefill.phone;

    const encoded = btoa(JSON.stringify(payload));
    return `${origin}/w/${encoded}`;
  }

  /**
   * Decode a short signing link payload.
   */
  decodeShortLink(encoded: string): { waiverId: string; name?: string; email?: string; phone?: string } | null {
    try {
      const payload = JSON.parse(atob(encoded));
      return {
        waiverId: payload.id,
        name: payload.n || '',
        email: payload.e || '',
        phone: payload.p || '',
      };
    } catch {
      return null;
    }
  }

  /**
   * Send a waiver signing link via email using the Firestore email queue.
   */
  async sendWaiverEmail(recipientEmail: string, recipientName: string, waiverName: string, link: string, companyIdOverride?: string): Promise<void> {
    const companyId = companyIdOverride || this.currentUserService.currentUser?.companyId;
    if (!companyId) {
      throw new Error('Company ID not available. Please log in and try again.');
    }

    const subject = `Action Required: Please sign "${waiverName}"`;
    const body = `
      <p>Hello${recipientName ? ' ' + recipientName : ''},</p>
      <p>You have been asked to review and sign the following waiver: <strong>${waiverName}</strong>.</p>
      <p>Please click the link below to open the waiver and complete your signature:</p>
      <p><a href="${link}" style="display:inline-block;padding:10px 20px;background-color:#0d6efd;color:#ffffff;text-decoration:none;border-radius:4px;font-weight:500;">Sign Waiver</a></p>
      <p>Or copy and paste this URL into your browser:<br><a href="${link}">${link}</a></p>
    `.trim();

    await this.firestoreService.createDocument(Collection.Emails, {
      emailTo: recipientEmail,
      emailSubject: subject,
      emailBody: body,
      companyID: companyId,
    });
  }

  /**
   * Stub: SMS delivery isn't wired up yet. The send-link modal exposes the
   * SMS option for UX continuity, but this method always throws so the UI
   * can show a "copy the link manually" fallback. Intentionally kept here
   * (not deleted) so the call site doesn't need a feature-flag — when an
   * SMS provider lands, swap the body for the real implementation.
   */
  async sendWaiverSms(recipientPhone: string, recipientName: string, waiverName: string, link: string): Promise<void> {
    const companyId = this.currentUserService.currentUser?.companyId;
    if (!companyId) {
      throw new Error('Company ID not available. Please log in and try again.');
    }
    console.warn('SMS sending is not yet configured. Phone:', recipientPhone, 'Link:', link);
    throw new Error('SMS sending is not yet configured. Please copy the link and send it manually.');
  }

  private generateId(): string {
    return 'field_' + Math.random().toString(36).substring(2, 11) + '_' + Date.now();
  }
}
